import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { useDeferredValue, useMemo, useState } from 'react';
import { SearchInput } from '../search-input';
import { DataCollectionGroupCard } from './group-card';
import type { DataCollectionGroup } from './types';

export interface DataCollectionGroupContainerProps {
  data: DataCollectionGroup[];
  getGroupLink: (group: DataCollectionGroup) => string;
}

export function DataCollectionGroupContainer({
  data,
  getGroupLink,
}: DataCollectionGroupContainerProps) {
  const { t } = useTranslation(['common', 'navigation']);
  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);

  const filteredList = useMemo(() => {
    if (!deferredSearchValue) return data;

    const lowercaseValue = deferredSearchValue.toLowerCase();
    return data.filter(({ title, description }) => {
      return (
        title.toLowerCase().includes(lowercaseValue) ||
        description.toLowerCase().includes(lowercaseValue)
      );
    });
  }, [deferredSearchValue, data]);

  return (
    <Card
      variant="soft"
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        padding: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography variant="h6" component="h2">
          {t('navigation:management.reports')}
        </Typography>
        <SearchInput
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </Stack>
      {filteredList.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t('common:noResultsFound')}
          </Typography>
        </Box>
      ) : (
        <Grid
          container
          spacing={2}
          sx={{
            '& .MuiGrid-root > div': {
              boxShadow: 'none',
              flexGrow: 1,

              '& > a': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minHeight: 80,
                textDecoration: 'none',
                p: 2,

                '& > .MuiCardActionArea-focusHighlight, & > .MuiTouchRipple-root':
                  {
                    borderRadius: 1.75,
                  },
              },
            },
          }}
        >
          {filteredList.map((report) => {
            return (
              <DataCollectionGroupCard
                key={report.id}
                group={report}
                getGroupLink={getGroupLink}
              />
            );
          })}
        </Grid>
      )}
    </Card>
  );
}
